import React, { useEffect, useState } from 'react';
import { View, Text, Platform, TouchableOpacity, StyleSheet, Alert } from 'react-native';
import { getNonTaxableTotal, getTotal, mapJobDateCrewDetailForFrontend } from './types';
import { Small } from '../ui/typography';
import { Card, CardContent } from '../ui/card';
import { Button, Separator } from '../ui';
import { Ionicons } from '@expo/vector-icons';
import * as Linking from 'expo-linking';
import { useCrew } from '@/context/CrewProvider';
import { formatDate, getPostcodeOutwardCode } from 'shared-utils';
import { isPast, addHours, isBefore } from 'date-fns'
import { useRouter } from "expo-router";
import { useSettings } from '@/hooks/useSettings';
import { JobDatesCrewList } from '../types/JobDateCrewList';
import { CrewList } from '../types/CrewList';
import { AccidentList } from '../types/Accident';
import { supabase } from '@/lib/supabase';
import { Image } from 'react-native';
import { VehicleList } from '../types/Vehicle';
import { crewCancellationCharges } from '../calendar/types';
import colors from '@/constants/Colors';
import { DocumentList } from '../types/Document';
import { MessageDocumentList } from '../types/MessageDocumentList';
import Toast from 'react-native-toast-message';
import * as FileSystem from 'expo-file-system/legacy';
import * as Sharing from 'expo-sharing';
import * as Clipboard from 'expo-clipboard';
import { EmailDocumentList } from '../types/EmailDocumentList';
import { Witness } from "../accident/types";
import { trpc } from '@/trpc/react';

type Document = DocumentList[0] | MessageDocumentList[0] | EmailDocumentList[0];

interface JobDetailItemProps {
  item: JobDatesCrewList | undefined | null;
  crewList: CrewList
  accidentList: AccidentList | undefined | null
  vehicles: VehicleList | undefined | null
  hideShowDates?: boolean
  documents: DocumentList | undefined | null
  messageDocuments: MessageDocumentList | undefined | null
  emailDocuments: EmailDocumentList | undefined | null
}

export const JobDetailItem = ({ item, crewList, accidentList, vehicles, hideShowDates = false, documents, messageDocuments, emailDocuments }: JobDetailItemProps) => {
  const [crewImages, setCrewImages] = useState<{ [key: string]: string }>({})
  const [isCreatingGroupMessage, setIsCreatingGroupMessage] = useState(false);
  const { currentCrewMember } = useCrew();
  const extraHoursRate = useSettings('extra-hours');
  const router = useRouter();

  const createGroupConversation = trpc.messages.createJobDateGroupConversation.useMutation({
    onSuccess: (data) => {
      setIsCreatingGroupMessage(false);
      Toast.show({
        type: 'success',
        text1: 'Group conversation created',
        text2: 'Opening conversation...'
      });
      // Navigate to the new conversation
      if (data && data.id) {
        router.push(`/(app)/messages/${data.id}`);
      }
    },
    onError: (err) => {
      console.error("Failed to create group conversation:", err);
      setIsCreatingGroupMessage(false);
      Alert.alert(
        "Error",
        err.message || "Failed to create group conversation. Please try again."
      );
    }
  });

  if (!item) {
    return (
      <View style={styles.notFoundContainer}>
        <Text style={styles.notFoundText}>Date not found</Text>
      </View>
    );
  }

  const currentCrewJob = crewList?.find((crew) => crew.crew_id === currentCrewMember?.id);
  const isFlatRate = currentCrewMember?.crew_levels?.[0]?.level?.can_provide_feedback === true;
  const mappedItem = mapJobDateCrewDetailForFrontend(item, { isFlatRate });

  const getCrewImages = async () => {
    const imageUrls: { [key: string]: string } = {};

    for (const crew of crewList) {
      if (crew.crew_id) {
        // Try with .jpeg extension first
        let { data } = await supabase.storage
          .from('crew')
          .createSignedUrl(`${crew.crew_id}/photos/photo.jpeg`, 100);

        // If that doesn't work, try without extension
        if (data === null) {
          const result = await supabase.storage
            .from('crew')
            .createSignedUrl(`${crew.crew_id}/photos/photo`, 100);
          data = result.data;
        }

        if (data?.signedUrl) {
          imageUrls[crew.crew_id] = data.signedUrl;
        }
      }
    }

    setCrewImages(imageUrls);
  }

  useEffect(() => {
    void getCrewImages()
  }, [crewList])

  const isPastDate = mappedItem.shiftStart ? isPast(mappedItem.shiftStart) : false;
  // Show button if shift has started and current time is within 3 hours after shift end
  const isWithin3HoursOfShiftEnd = mappedItem.shiftEnd ? isBefore(new Date(), addHours(mappedItem.shiftEnd, 3)) : false;
  const hasNotSubmittedFinishedEarly = !currentCrewJob?.finished_early_hours;
  const showFinishedEarlyButton = isPastDate && isWithin3HoursOfShiftEnd && hasNotSubmittedFinishedEarly;

  const canProvideFeedback = currentCrewJob?.is_chief || currentCrewMember?.crew_levels?.[0]?.level?.can_provide_feedback === true;
  const canProvideProbationChecklist = currentCrewJob?.is_chief || currentCrewMember?.crew_levels?.[0]?.level?.can_provide_probation_checklist === true;

  const allFeedbackComplete = crewList.every((crew) => crew.is_chief || crew.job_crew_feedback?.find((feedback) => feedback.author_id === currentCrewMember?.id));

  const showFeedbackBonusMissedBanner = currentCrewJob?.is_chief && isPastDate && mappedItem.feedbackTimePast && !allFeedbackComplete;
  const showFeedbackToBeCompletedBanner = currentCrewJob?.is_chief && isPastDate && !mappedItem.feedbackTimePast && !allFeedbackComplete;

  const getsOotBonus = currentCrewMember?.crew_levels?.[0]?.level?.gets_oot_bonus === true;

  const payTotal = getTotal(mappedItem, currentCrewJob, extraHoursRate, getsOotBonus);
  const nonTaxableTotal = getNonTaxableTotal(mappedItem, currentCrewJob);

  const crewCancelledCharge = crewCancellationCharges.find(c => c.value === mappedItem.crew_cancelled_charge)?.label || 'Unknown';

  const navigateToReport = () => {
    router.push({
      pathname: "/home/[id]/report",
      params: { id: mappedItem.id.toString() }
    });
  };

  const navigateToFinishedEarly = () => {
    router.push({
      pathname: "/home/[id]/finished-early",
      params: { id: mappedItem.id.toString() }
    });
  };

  const navigateToClientFeedback = () => {
    router.push({
      pathname: "/home/[id]/feedback/client",
      params: {
        id: mappedItem.id.toString(),
      }
    });
  };

  const navigateToVenueFeedback = () => {

    router.push({
      pathname: "/home/[id]/feedback/venue",
      params: {
        id: mappedItem.id.toString(),
      }
    });
  };

  const navigateToFeedback = (crewItemId: number, crewName?: string) => {
    router.push({
      pathname: "/home/[id]/feedback/[crewId]",
      params: {
        id: mappedItem.id.toString(),
        crewId: crewItemId.toString(),
        query: crewName
      }
    });
  };

  const navigateToProbationChecklist = (crewMemberId: number, crewName?: string) => {
    router.push({
      pathname: "/home/[id]/checklist/[crewId]",
      params: {
        id: mappedItem.id.toString(),
        crewId: crewMemberId.toString(),
        query: crewName
      }
    });
  };

  const handleCreateGroupMessage = () => {
    if (!currentCrewMember?.id) {
      Alert.alert("Error", "You must be logged in to create a group conversation.");
      return;
    }

    setIsCreatingGroupMessage(true);
    createGroupConversation.mutate({
      job_date_id: mappedItem.id,
      crew_id: currentCrewMember.id,
      initial_message: `Group conversation started by ${currentCrewMember.name}`
    });
  };

  const taxableMisc = mappedItem.misc_skills?.filter((skill) => !skill.is_custom && skill.skill.non_taxible !== true).reduce((acc, curr) => acc + Number(curr.value || 0), 0) || 0;
  const nonTaxableMisc = mappedItem.misc_skills?.filter((skill) => skill.is_custom || skill.skill.non_taxible === true).reduce((acc, curr) => acc + Number(curr.value || 0), 0) || 0;

  return (
    <View style={styles.container}>
      {/* Error banners */}
      {showFeedbackBonusMissedBanner && (
        <View style={styles.errorNoticeContainer}>
          <View style={styles.errorNotice}>
            <Ionicons name="alert-circle" size={22} color="#FF6B6B" />
            <Text style={styles.errorText}>
              You have missed out on bonus because you did not fill in feedback within 24 hours of shift ending!
            </Text>
          </View>
        </View>
      )}

      {showFeedbackToBeCompletedBanner && (
        <View style={styles.errorNoticeContainer}>
          <View style={styles.errorNotice}>
            <Ionicons name="alert-circle" size={22} color="#FF6B6B" />
            <Text style={styles.errorText}>
              You have not filled in feedback for all crew members, please do so within 24 hours of shift ending to receive bonus!
            </Text>
          </View>
        </View>
      )}

      {mappedItem.cancelled && (
        <View style={styles.errorNoticeContainer}>
          <View style={styles.errorNotice}>
            <Ionicons name="alert-circle" size={22} color="#FF6B6B" />
            <Text style={styles.errorText}>
              This date has been cancelled
              {mappedItem.cancelled_charge !== 3
                ? ` - ${crewCancelledCharge}`
                : ` - £${mappedItem.cancel_crew_amount}`
              }
            </Text>
          </View>
        </View>
      )}

      {/* Date and Time info */}
      <View style={styles.dateHeaderContainer}>
        <Text style={styles.dateText}>{mappedItem.date} - {mappedItem.hours}hrs</Text>
        <Text style={styles.timeText}>{mappedItem.start_time} - {mappedItem.end_time}</Text>
        {showFinishedEarlyButton && (
          <Button
            variant="success"
            style={styles.finishedEarlyButton}
            onPress={navigateToFinishedEarly}
          >
            <Ionicons name="time-outline" color="white" size={16} style={styles.buttonIcon} />
            <Text style={styles.finishedEarlyButtonText}>I Finished Early</Text>
          </Button>
        )}
      </View>

      <Separator style={styles.separator} />

      {/* Venue card */}
      <Card style={styles.card}>
        <View style={styles.cardHeader}>
          <View style={styles.iconWrapper}>
            <Ionicons name="business" size={18} color="white" />
          </View>
          <Text style={styles.cardTitle}>Venue</Text>
        </View>
        <CardContent style={styles.cardContent}>
          <Text style={styles.clientName}>{mappedItem.client_name}</Text>

          {/* Address  */}
          {(mappedItem.venue_address_line_1 || mappedItem.venue_address_postcode) && (
            <View style={styles.noteContainer}>
              <View style={styles.noteHeader}>
                <Text style={styles.noteLabel} >Address</Text>
                <TouchableOpacity
                  style={styles.copyButton}
                  onPress={async () => {
                    const addressParts = [];

                    if (mappedItem.venue_name) {
                      addressParts.push(mappedItem.venue_name);
                    }

                    if (mappedItem.venue_address_line_1) {
                      addressParts.push(mappedItem.venue_address_line_1);
                    }

                    if (mappedItem.venue_address_line_2) {
                      addressParts.push(mappedItem.venue_address_line_2);
                    }

                    if (mappedItem.venue_address_line_3) {
                      addressParts.push(mappedItem.venue_address_line_3);
                    }

                    if (mappedItem.venue_address_postcode) {
                      addressParts.push(mappedItem.venue_address_postcode);
                    }

                    const fullAddress = addressParts.join(', ');
                    await Clipboard.setStringAsync(fullAddress);
                    Toast.show({
                      type: 'success',
                      text1: 'Copied to clipboard',
                      text2: 'Address copied'
                    });
                  }}
                >
                  <Ionicons name="copy-outline" size={16} color={colors.primary} />
                  <Text style={styles.copyButtonText}>Copy</Text>
                </TouchableOpacity>
              </View>
              <Text
                style={styles.noteText}
                selectable={true}
              >
                {mappedItem.venue_name ? `${mappedItem.venue_name}\n` : ''}
                {mappedItem.venue_address_line_1}
                {mappedItem.venue_address_line_2 ? `\n${mappedItem.venue_address_line_2}` : ''}
                {mappedItem.venue_address_line_3 ? `\n${mappedItem.venue_address_line_3}` : ''}
                {mappedItem.venue_address_postcode ? `\n${mappedItem.venue_address_postcode}` : ''}
              </Text>
            </View>
          )}

          {mappedItem.venue_what_three_words && (
            <View style={styles.noteContainer}>
              <View style={styles.noteHeader}>
                <Text style={styles.noteLabel}>What3Words</Text>
                <TouchableOpacity
                  style={styles.copyButton}
                  onPress={async () => {
                    const words = mappedItem.venue_what_three_words.replace(/^\/\/\//, '').trim();
                    await Clipboard.setStringAsync(words);
                    Toast.show({
                      type: 'success',
                      text1: 'Copied to clipboard',
                      text2: 'What3Words copied'
                    });
                  }}
                >
                  <Ionicons name="copy-outline" size={16} color={colors.primary} />
                  <Text style={styles.copyButtonText}>Copy</Text>
                </TouchableOpacity>
              </View>
              <Text
                style={styles.noteText}
                selectable={true}
              >
                {mappedItem.venue_what_three_words.replace(/^\/\/\//, '')}
              </Text>
            </View>
          )}

          {mappedItem.client_contact_one_name || mappedItem.client_contact_two_name || mappedItem.client_date_contact_name ? (
            <View style={styles.contactSection}>
              <Text style={styles.sectionLabel}>Contact</Text>

              {mappedItem.client_contact_one_name && (
                <View style={styles.contactItem}>
                  <Text style={styles.contactName}>{mappedItem.client_contact_one_name}</Text>
                  {mappedItem.client_contact_one_mobile && (
                    <Button
                      variant="crew"
                      style={styles.contactButton}
                      onPress={() => Linking.openURL(`tel:${mappedItem.client_contact_one_mobile}`)}
                    >
                      <Ionicons name="phone-portrait-outline" color="#FFF000" size={16} />
                      <Small style={styles.buttonText}>{mappedItem.client_contact_one_mobile}</Small>
                    </Button>
                  )}
                </View>
              )}

              {mappedItem.client_contact_two_name && (
                <View style={styles.contactItem}>
                  <Text style={styles.contactName}>{mappedItem.client_contact_two_name}</Text>
                  {mappedItem.client_contact_two_mobile && (
                    <Button
                      variant="crew"
                      style={styles.contactButton}
                      onPress={() => Linking.openURL(`tel:+${mappedItem.client_contact_two_mobile}`)}
                    >
                      <Ionicons name="phone-portrait-outline" color="#FFF000" size={16} />
                      <Small style={styles.buttonText}>{mappedItem.client_contact_two_mobile}</Small>
                    </Button>
                  )}
                </View>
              )}

              {mappedItem.client_date_contact_name && (
                <View style={styles.contactItem}>
                  <Text style={styles.contactName}>{mappedItem.client_date_contact_name}</Text>
                  {mappedItem.client_date_contact_mobile && (
                    <Button
                      variant="crew"
                      style={styles.contactButton}
                      onPress={() => Linking.openURL(`tel:${mappedItem.client_date_contact_mobile}`)}
                    >
                      <Ionicons name="phone-portrait-outline" color="#FFF000" size={16} />
                      <Small style={styles.buttonText}>{mappedItem.client_date_contact_mobile}</Small>
                    </Button>
                  )}
                </View>
              )}
            </View>
          ) : null}

          <View style={styles.actionsContainer}>
            {/* Navigation buttons - compact row layout */}
            {((mappedItem.venue_address_line_1 || mappedItem.venue_address_postcode) ||
              mappedItem.venue_what_three_words ||
              (mappedItem.venue_what_three_words_lat && mappedItem.venue_what_three_words_lng)) && (
                <View style={styles.navigationSection}>
                  <Text style={styles.actionSectionLabel}>Navigation</Text>
                  <View style={styles.compactButtonRow}>
                    {(mappedItem.venue_address_line_1 || mappedItem.venue_address_postcode) && (
                      <Button
                        variant="crew"
                        style={styles.compactButton}
                        onPress={() => {
                          const address = `${mappedItem.venue_address_postcode}`;
                          const encodedAddress = encodeURIComponent(address);

                          // Different URL schemes for iOS and Android
                          const url = Platform.select({
                            ios: `maps://app?daddr=${encodedAddress}`,
                            android: `google.navigation:q=${encodedAddress}`,
                            default: `https://maps.google.com/maps?daddr=${encodedAddress}`
                          });

                          Linking.openURL(url).catch(err => {
                            console.error('An error occurred', err);
                            // Fallback for when no maps app is available
                            Linking.openURL(`https://maps.google.com/maps?daddr=${encodedAddress}`);
                          });
                        }}
                      >
                        <Ionicons name="compass-outline" color="#FFF000" size={16} style={styles.buttonIcon} />
                        <Text style={styles.buttonText}>Directions</Text>
                      </Button>
                    )}

                    {mappedItem.venue_what_three_words_lat && mappedItem.venue_what_three_words_lng && (
                      <Button
                        variant="crew"
                        style={styles.compactButton}
                        onPress={() => {
                          const coords = `${mappedItem.venue_what_three_words_lat},${mappedItem.venue_what_three_words_lng}`;

                          // Use Google Maps with the lat/long coordinates from W3W
                          const url = Platform.select({
                            ios: `maps://app?daddr=${coords}`,
                            android: `google.navigation:q=${coords}`,
                            default: `https://maps.google.com/maps?daddr=${coords}`
                          });

                          Linking.openURL(url).catch(err => {
                            console.error('An error occurred', err);
                            // Fallback for when no maps app is available
                            Linking.openURL(`https://maps.google.com/maps?daddr=${coords}`);
                          });
                        }}
                      >
                        <Ionicons name="compass-outline" color="#FFF000" size={16} style={styles.buttonIcon} />
                        <Text style={styles.buttonText}>What3Words</Text>
                      </Button>
                    )}
                  </View>
                </View>
              )}

            {/* Feedback buttons - grouped section for past dates */}
            {isPastDate && (
              <View style={styles.feedbackSection}>
                <Text style={styles.actionSectionLabel}>Post-Job Actions</Text>
                <View style={styles.compactButtonColumn}>
                  {canProvideFeedback && (
                    <>
                      <Button
                        variant="crew"
                        style={styles.compactVerticalButton}
                        onPress={navigateToClientFeedback}
                      >
                        <Ionicons name="pencil-outline" color="#FFF000" size={16} style={styles.buttonIcon} />
                        <Text style={styles.buttonText}>Client Feedback</Text>
                      </Button>
                      <Button
                        variant="crew"
                        style={styles.compactVerticalButton}
                        onPress={navigateToVenueFeedback}
                      >
                        <Ionicons name="pencil-outline" color="#FFF000" size={16} style={styles.buttonIcon} />
                        <Text style={styles.buttonText}>Venue Feedback</Text>
                      </Button>
                    </>
                  )}
                  <Button
                    variant="crew"
                    style={styles.compactVerticalButton}
                    onPress={navigateToReport}
                  >
                    <Ionicons name="medkit-outline" color="#FFF000" size={16} style={styles.buttonIcon} />
                    <Text style={styles.buttonText}>Report Accident</Text>
                  </Button>
                </View>
              </View>
            )}
          </View>

          {accidentList && accidentList.length > 0 && (
            <View style={styles.accidentsSection}>
              <Text style={styles.sectionLabel}>Accidents reported on this job:</Text>
              {accidentList.map((accident) => (
                <Text key={accident.id.toString()} style={styles.accidentItem}>
                  • {accident.crew?.name || accident.name || (Array.isArray(accident.witnesses) ? (accident.witnesses as Witness[])[0]?.name : '') || accident?.witness_name} - {formatDate(accident.date, 'EEEE dd/MM/yyyy')}
                </Text>
              ))}
            </View>
          )}

        </CardContent>
      </Card>

      {/* Notes card */}
      <Card style={styles.card}>
        <View style={styles.cardHeader}>
          <View style={styles.iconWrapper}>
            <Ionicons name="document-text" size={18} color="white" />
          </View>
          <Text style={styles.cardTitle}>Notes</Text>
        </View>
        <CardContent style={styles.cardContent}>
          {mappedItem.venue_client_notes ? (
            <View style={styles.noteContainer}>
              <View style={styles.noteHeader}>
                <Text style={styles.noteLabel}>Client Venue Notes</Text>
                <TouchableOpacity
                  style={styles.copyButton}
                  onPress={async () => {
                    await Clipboard.setStringAsync(mappedItem.venue_client_notes);
                    Toast.show({
                      type: 'success',
                      text1: 'Copied to clipboard',
                      text2: 'Client venue notes copied'
                    });
                  }}
                >
                  <Ionicons name="copy-outline" size={16} color={colors.primary} />
                  <Text style={styles.copyButtonText}>Copy</Text>
                </TouchableOpacity>
              </View>
              <Text
                style={styles.noteText}
                selectable={true}
              >
                {mappedItem.venue_client_notes}
              </Text>
            </View>
          ) : null}

          {mappedItem.venue_crew_notes ? (
            <View style={styles.noteContainer}>
              <View style={styles.noteHeader}>
                <Text style={styles.noteLabel}>Crew Venue Notes</Text>
                <TouchableOpacity
                  style={styles.copyButton}
                  onPress={async () => {
                    await Clipboard.setStringAsync(mappedItem.venue_crew_notes);
                    Toast.show({
                      type: 'success',
                      text1: 'Copied to clipboard',
                      text2: 'Crew venue notes copied'
                    });
                  }}
                >
                  <Ionicons name="copy-outline" size={16} color={colors.primary} />
                  <Text style={styles.copyButtonText}>Copy</Text>
                </TouchableOpacity>
              </View>
              <Text
                style={styles.noteText}
                selectable={true}
              >
                {mappedItem.venue_crew_notes}
              </Text>
            </View>
          ) : null}

          {mappedItem.date_crew_notes ? (
            <View style={styles.noteContainer}>
              <View style={styles.noteHeader}>
                <Text style={styles.noteLabel}>Date Notes</Text>
                <TouchableOpacity
                  style={styles.copyButton}
                  onPress={async () => {
                    await Clipboard.setStringAsync(mappedItem.date_crew_notes);
                    Toast.show({
                      type: 'success',
                      text1: 'Copied to clipboard',
                      text2: 'Date notes copied'
                    });
                  }}
                >
                  <Ionicons name="copy-outline" size={16} color={colors.primary} />
                  <Text style={styles.copyButtonText}>Copy</Text>
                </TouchableOpacity>
              </View>
              <Text
                style={styles.noteText}
                selectable={true}
              >
                {mappedItem.date_crew_notes}
              </Text>
            </View>
          ) : null}

          {mappedItem.date_crew_chief_notes && currentCrewJob?.is_chief && (
            <View style={styles.noteContainer}>
              <View style={styles.noteHeader}>
                <Text style={styles.noteLabel}>Crew Chief Notes</Text>
                <TouchableOpacity
                  style={styles.copyButton}
                  onPress={async () => {
                    await Clipboard.setStringAsync(mappedItem.date_crew_chief_notes);
                    Toast.show({
                      type: 'success',
                      text1: 'Copied to clipboard',
                      text2: 'Crew chief notes copied'
                    });
                  }}
                >
                  <Ionicons name="copy-outline" size={16} color={colors.primary} />
                  <Text style={styles.copyButtonText}>Copy</Text>
                </TouchableOpacity>
              </View>
              <Text
                style={styles.noteText}
                selectable={true}
              >
                {mappedItem.date_crew_chief_notes}
              </Text>
            </View>
          )}

          {!mappedItem.date_crew_notes && !mappedItem.venue_client_notes && !mappedItem.venue_crew_notes && !currentCrewJob?.is_chief ? (
            <Text style={styles.noteText}>No notes</Text>
          ) : null}

          {currentCrewJob?.is_chief && !mappedItem.date_crew_notes && !mappedItem.venue_client_notes && !mappedItem.venue_crew_notes && !mappedItem.date_crew_chief_notes ? (
            <Text style={styles.noteText}>No notes</Text>
          ) : null}
        </CardContent>
      </Card>

      {/* Misc skills card */}
      {mappedItem.misc_skills && mappedItem.misc_skills.length > 0 && currentCrewJob && (
        <>
          {/* Filter skills to only show those that match the current crew member's job_crew_id */}
          {mappedItem.misc_skills.filter(skill => skill.job_crew_id === currentCrewJob.id).length > 0 && (
            <Card style={styles.card}>
              <View style={styles.cardHeader}>
                <View style={styles.iconWrapper}>
                  <Ionicons name="build-outline" size={18} color="white" />
                </View>
                <Text style={styles.cardTitle}>Skills</Text>
              </View>
              <CardContent style={styles.cardContent}>
                {mappedItem.misc_skills
                  .filter(skill => skill.job_crew_id === currentCrewJob.id)
                  .map((skill) => (
                    <Text key={skill.name} style={styles.noteText}>• {skill.name}</Text>
                  ))}
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Documents card */}
      <Card style={styles.card}>
        <View style={styles.cardHeader}>
          <View style={styles.iconWrapper}>
            <Ionicons name="document-text" size={18} color="white" />
          </View>
          <Text style={styles.cardTitle}>Documents</Text>
        </View>
        <CardContent style={styles.cardContent}>
          {((documents && documents.length > 0) || (messageDocuments && messageDocuments.length > 0) || (emailDocuments && emailDocuments.length > 0)) ? (
            <View>
              {[...(documents || []), ...(messageDocuments || []), ...(emailDocuments || [])].map((doc: Document) => (
                <TouchableOpacity
                  key={doc.id}
                  style={styles.documentItem}
                  onPress={async () => {
                    try {
                      const isMessageDoc = 'file_url' in doc;
                      const isEmailDoc = 'booking_email_id' in doc;
                      let bucket;
                      if (isMessageDoc) {
                        bucket = 'group-messages';
                      } else if (isEmailDoc) {
                        bucket = 'messages';
                      } else {
                        bucket = 'jobs';
                      }
                      const filePath = isMessageDoc ? doc.file_url : doc.file_blob_location;

                      const { data, error } = await supabase.storage
                        .from(bucket)
                        .download(filePath);

                      if (error) {
                        Toast.show({
                          type: 'error',
                          text1: 'Error downloading file'
                        });
                        return;
                      }

                      // Create a temporary file path
                      const fileUri = FileSystem.documentDirectory + doc.file_name;

                      // Convert the downloaded data to a base64 string
                      const fr = new FileReader();
                      fr.onload = async () => {
                        const base64Data = (fr.result as string).split(',')[1];

                        try {
                          // Write the file to the file system
                          await FileSystem.writeAsStringAsync(fileUri, base64Data, {
                            encoding: FileSystem.EncodingType.Base64,
                          });

                          // Share/open the file
                          const canShare = await Sharing.isAvailableAsync();
                          if (canShare) {
                            await Sharing.shareAsync(fileUri, {
                              mimeType: doc.file_type,
                              dialogTitle: doc.file_name,
                            });
                          } else {
                            Toast.show({
                              type: 'error',
                              text1: 'Sharing is not available on this device'
                            });
                          }

                          // Clean up the temporary file
                          await FileSystem.deleteAsync(fileUri, { idempotent: true });
                        } catch (err) {
                          Toast.show({
                            type: 'error',
                            text1: 'Error opening file'
                          });
                        }
                      };

                      fr.readAsDataURL(data);
                    } catch (err) {
                      Toast.show({
                        type: 'error',
                        text1: 'Error downloading file'
                      });
                    }
                  }}
                >
                  <View style={styles.documentIconContainer}>
                    <Ionicons name="document-text-outline" size={24} color={colors.primary} />
                  </View>
                  <View style={styles.documentInfo}>
                    <Text style={styles.documentName}>{doc.file_name}</Text>
                    <Text style={styles.documentSize}>
                      {doc.file_size ? `${Math.round(doc.file_size / 1024)}KB` : 'Unknown size'}
                    </Text>
                  </View>
                  <Ionicons name="download-outline" size={24} color={colors.primary} />
                </TouchableOpacity>
              ))}
            </View>
          ) : (
            <Text style={styles.noteText}>No documents available</Text>
          )}
        </CardContent>
      </Card>

      {/* Pay card */}
      <Card style={styles.card}>
        <View style={styles.cardHeader}>
          <View style={styles.iconWrapper}>
            <Ionicons name="cash" size={18} color="white" />
          </View>
          <Text style={styles.cardTitle}>Pay</Text>
        </View>
        <CardContent style={styles.cardContent}>
          <View style={styles.payItem}>
            <Text style={styles.payLabel}>Hours:</Text>
            <Text style={styles.payValue}>{mappedItem.hoursTotal.toFixed(2)}</Text>
          </View>

          {(taxableMisc > 0) && (
            <View style={styles.payItem}>
              <Text style={styles.payLabel}>Skills (Taxable):</Text>
              <Text style={styles.payValue}>{Number(taxableMisc).toFixed(2)}</Text>
            </View>
          )}

          {(nonTaxableMisc > 0) && (
            <View style={styles.payItem}>
              <Text style={styles.payLabel}>Skills (Non-Taxable):</Text>
              <Text style={styles.payValue}>{Number(nonTaxableMisc).toFixed(2)}</Text>
            </View>
          )}

          {mappedItem.experienceBonus ? (
            <View style={styles.payItem}>
              <Text style={styles.payLabel}>Founders Bonus:</Text>
              <Text style={styles.payValue}>{mappedItem.experienceBonus?.toFixed(2)}</Text>
            </View>
          ) : null}

          {mappedItem.job_date_out_of_town_bonus_crew && getsOotBonus && !mappedItem.zero_travel_expense && (
            <View style={styles.payItem}>
              <Text style={styles.payLabel}>OOT:</Text>
              <Text style={styles.payValue}>{mappedItem.job_date_out_of_town_bonus_crew.toFixed(2)}</Text>
            </View>
          )}

          {currentCrewJob?.drive && (
            <View style={styles.payItem}>
              <Text style={styles.payLabel}>Mileage Fuel (Non-Taxable):</Text>
              <Text style={styles.payValue}>{Number(currentCrewJob.drive).toFixed(2)}</Text>
            </View>
          )}

          {currentCrewJob?.drive_bonus && (
            <View style={styles.payItem}>
              <Text style={styles.payLabel}>Driver Bonus:</Text>
              <Text style={styles.payValue}>{Number(currentCrewJob.drive_bonus).toFixed(2)}</Text>
            </View>
          )}

          {currentCrewJob?.is_chief && (
            <View style={styles.payItem}>
              <Text style={styles.payLabel}>Crew Chief Bonus:</Text>
              <Text style={styles.payValue}>{Number(currentCrewJob.chief).toFixed(2)}</Text>
            </View>
          )}

          {Number(currentCrewJob?.hours) > 0 && (
            <View style={styles.payItem}>
              <Text style={styles.payLabel}>Extra Hours ({Number(currentCrewJob?.hours)}):</Text>
              <Text style={styles.payValue}>{(Number(currentCrewJob?.hours) * Number(extraHoursRate)).toFixed(2)}</Text>
            </View>
          )}

          {currentCrewJob?.adjusted_hours && Number(currentCrewJob?.adjusted_hours) > 0 && (
            <View style={styles.payItem}>
              <Text style={styles.payLabel}>Adjusted Hours:</Text>
              <Text style={styles.payValue}>{Number(currentCrewJob?.adjusted_hours)} Hours</Text>
            </View>
          )}

          <View style={[styles.payItem, styles.totalPay, { flexDirection: 'column', alignItems: 'flex-start' }]}>
            <View style={{ flexDirection: 'row', width: '100%', justifyContent: 'space-between' }}>
              <Text style={styles.totalLabel}>Non-Taxable Total:</Text>
              <Text style={styles.totalValue}>{nonTaxableTotal.toFixed(2)}</Text>
            </View>
            <View style={{ flexDirection: 'row', width: '100%', justifyContent: 'space-between', marginTop: 4 }}>
              <Text style={styles.totalLabel}>Total:</Text>
              <Text style={styles.totalValue}>{payTotal.toFixed(2)}</Text>
            </View>
          </View>
        </CardContent>
      </Card>

      {/* Crew card */}
      {crewList && crewList.length > 0 && (
        <Card style={styles.card}>
          <View style={styles.cardHeader}>
            <View style={styles.iconWrapper}>
              <Ionicons name="people" size={18} color="white" />
            </View>
            <Text style={styles.cardTitle}>Crew</Text>
          </View>
          <CardContent style={styles.cardContent}>
            {/* Group Message Button - Only shown for crew chiefs */}
            {currentCrewJob?.is_chief && crewList.length > 1 && (
              <View style={styles.groupMessageSection}>
                <Button
                  variant="crew"
                  style={styles.groupMessageButton}
                  onPress={handleCreateGroupMessage}
                  disabled={isCreatingGroupMessage}
                >
                  {isCreatingGroupMessage ? (
                    <>
                      <Text style={styles.buttonText}>Creating...</Text>
                    </>
                  ) : (
                    <>
                      <Ionicons name="chatbubbles-outline" color="#FFF000" size={16} style={styles.buttonIcon} />
                      <Text style={styles.buttonText}>Create Group Message</Text>
                    </>
                  )}
                </Button>
              </View>
            )}
            {[...crewList]
              .sort((a, b) => {
                // Always put chief at the top
                if (a.is_chief) return -1;
                if (b.is_chief) return 1;

                // Sort by level rank in descending order
                const aRank = a.crew?.crew_levels?.[0]?.level?.rank || 0;
                const bRank = b.crew?.crew_levels?.[0]?.level?.rank || 0;
                return aRank - bRank;
              })
              .map((crewItem, index) => {
                const imageUrl = crewItem.crew_id ? crewImages[crewItem.crew_id] : undefined;
                const outwardCode = getPostcodeOutwardCode(crewItem.crew?.address_postcode);
                const hasCar = crewItem.crew?.has_car === true;
                return (
                  <View key={crewItem.id} style={[
                    styles.crewItem,
                    index < crewList.length - 1 && styles.crewItemBorder
                  ]}>
                    <View style={styles.crewHeader}>
                      <View style={styles.crewImageContainer}>
                        {imageUrl ? (
                          <Image
                            source={{ uri: imageUrl }}
                            style={styles.crewImage}
                            resizeMode="cover"
                          />
                        ) : (
                          <View style={styles.crewImagePlaceholder}>
                            <Ionicons name="person" size={35} color={colors.primary} />
                          </View>
                        )}
                      </View>
                      <View style={{ flex: 1, flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center' }}>
                        <View style={{ flex: 1 }}>
                          <Text style={styles.crewName}>{crewItem.crew?.name}</Text>
                          {crewItem.crew?.crew_levels && crewItem.crew.crew_levels[0]?.level && (
                            <Text style={styles.crewLevel}>{crewItem.crew.crew_levels[0].level.name}</Text>
                          )}
                          {(outwardCode || hasCar) && (
                            <View style={styles.crewLiftRow}>
                              {outwardCode ? (
                                <View style={styles.crewLiftBadge}>
                                  <Ionicons name="location" size={11} color="#475569" />
                                  <Text style={styles.crewLiftBadgeText}>{outwardCode}</Text>
                                </View>
                              ) : null}
                              {hasCar ? (
                                <View style={styles.crewLiftBadge}>
                                  <Ionicons name="car" size={11} color="#475569" />
                                  <Text style={styles.crewLiftBadgeText}>Car</Text>
                                </View>
                              ) : null}
                            </View>
                          )}
                        </View>
                        {crewItem.is_chief && (
                          <View style={styles.badgeContainer}>
                            <Text style={styles.badgeText}>CHIEF</Text>
                          </View>
                        )}
                        {crewItem.buddy && (
                          <View style={styles.badgeContainer}>
                            <Text style={styles.badgeText}>BUDDY</Text>
                          </View>
                        )}
                        {!crewItem.crew?.probation_checklist_completed && crewItem.crew?.crew_levels[0]?.level?.probation_checklist_required && (
                          <View style={styles.badgeContainer}>
                            <Text style={styles.badgeText}>PROBATION</Text>
                          </View>
                        )}
                      </View>
                    </View>

                    <View style={styles.crewButtons}>
                      <Button
                        variant="crew"
                        style={styles.crewButton}
                        onPress={() => Linking.openURL(`tel:${crewItem.crew?.mobile}`)}
                      >
                        <Ionicons name="phone-portrait-outline" color="#FFF000" size={16} style={styles.buttonIcon} />
                        <Small style={styles.buttonText}>{crewItem.crew?.mobile}</Small>
                      </Button>

                      <Button
                        variant="crew"
                        style={styles.crewButton}
                        onPress={() => Linking.openURL(`mailto:${crewItem.crew?.email}`)}
                      >
                        <Ionicons name="mail-outline" color="#FFF000" size={16} style={styles.buttonIcon} />
                        <Small style={styles.buttonText}>{crewItem.crew?.email}</Small>
                      </Button>
                    </View>

                    {crewItem.crew?.id !== currentCrewMember?.id && canProvideProbationChecklist && !crewItem.crew?.probation_checklist_completed && crewItem.crew?.crew_levels[0]?.level?.probation_checklist_required && (
                      <Button
                        variant="crew"
                        style={styles.crewButton}
                        onPress={() => navigateToProbationChecklist(crewItem.crew_id!, crewItem.crew?.name || "")}
                      >
                        <Ionicons name="list-outline" color="#FFF000" size={16} style={styles.buttonIcon} />
                        <Text style={styles.buttonText}>Probation Checklist</Text>
                      </Button>
                    )}

                    {crewItem.crew?.id !== currentCrewMember?.id && canProvideFeedback && isPastDate && !mappedItem.feedbackTimePast && !(crewItem.job_crew_feedback?.find((feedback) => feedback.author_id === currentCrewMember?.id)) && (
                      <Button
                        variant="crew"
                        style={styles.feedbackButton}
                        onPress={() => navigateToFeedback(crewItem.id, crewItem.crew?.name || "")}
                      >
                        <Ionicons name="pencil-outline" color="#FFF000" size={16} style={styles.buttonIcon} />
                        <Text style={styles.buttonText}>Feedback</Text>
                      </Button>
                    )}
                  </View>
                );
              })}
          </CardContent>
        </Card>
      )}

      {/* Vehicles card */}
      {vehicles && vehicles?.data?.length > 0 && (
        <Card style={styles.card}>
          <View style={styles.cardHeader}>
            <View style={styles.iconWrapper}>
              <Ionicons name="car" size={18} color="white" />
            </View>
            <Text style={styles.cardTitle}>Vehicles</Text>
          </View>
          <CardContent style={styles.cardContent}>
            {vehicles.data.map((v, index) => (
              <View
                key={v.id}
                style={[
                  styles.vehicleItem,
                  index < vehicles.data.length - 1 && styles.vehicleItemBorder
                ]}
              >
                <View style={styles.vehicleDetail}>
                  <Text style={styles.vehicleLabel}>Registration:</Text>
                  <Text style={styles.vehicleValue}>{v.vehicle.registration}</Text>
                </View>

                <View style={styles.vehicleDetail}>
                  <Text style={styles.vehicleLabel}>Make:</Text>
                  <Text style={styles.vehicleValue}>{v.vehicle.make}</Text>
                </View>

                <View style={styles.vehicleDetail}>
                  <Text style={styles.vehicleLabel}>Model:</Text>
                  <Text style={styles.vehicleValue}>{v.vehicle.model}</Text>
                </View>

                <View style={styles.vehicleDetail}>
                  <Text style={styles.vehicleLabel}>Drivers:</Text>
                  <Text style={styles.vehicleValue}>{v.drivers.map((driver) => driver.driver.name).join(", ")}</Text>
                </View>
              </View>
            ))}
          </CardContent>
        </Card>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingBottom: 20,
  },
  notFoundContainer: {
    height: 56,
    justifyContent: 'center',
    paddingLeft: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#ddd',
  },
  notFoundText: {
    fontSize: 18,
    color: '#666',
  },
  errorNoticeContainer: {
    marginBottom: 12,
  },
  errorNotice: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderWidth: 1,
    borderColor: '#FF6B6B',
    borderRadius: 10,
    backgroundColor: '#FFF5F5',
  },
  errorText: {
    flex: 1,
    marginLeft: 12,
    fontSize: 14,
    fontWeight: '500',
    color: '#FF6B6B',
    flexWrap: 'wrap',
  },
  dateHeaderContainer: {
    paddingVertical: 6,
    paddingHorizontal: 4,
  },
  dateText: {
    fontSize: 16,
    color: '#333',
    marginBottom: 4,
  },
  timeText: {
    fontSize: 24,
    fontWeight: '700',
    color: '#000',
    marginBottom: 8,
  },
  finishedEarlyButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    minHeight: 44,
    marginTop: 8,
    alignSelf: 'flex-start',
  },
  finishedEarlyButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
    textTransform: 'uppercase',
  },
  separator: {
    marginVertical: 12,
    backgroundColor: '#ddd',
    height: 1,
  },
  showAllDatesButton: {
    paddingVertical: 10,
    marginBottom: 16,
  },
  showAllDatesText: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.primary,
  },
  card: {
    borderRadius: 12,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 2,
    borderWidth: 1,
    borderColor: '#eee',
    overflow: 'hidden',
    backgroundColor: '#fff',
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  iconWrapper: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#000',
    marginLeft: 8,
  },
  cardContent: {
    marginTop: 16,
    padding: 16,
  },
  clientName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#000',
    marginBottom: 4,
  },
  venueText: {
    fontSize: 15,
    color: '#333',
    marginBottom: 2,
  },
  addressText: {
    fontSize: 14,
    color: '#666',
    marginBottom: 16,
  },
  contactSection: {
    marginBottom: 16,
  },
  sectionLabel: {
    fontSize: 15,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
  },
  contactItem: {
    marginBottom: 12,
  },
  contactName: {
    fontSize: 14,
    color: '#333',
    marginBottom: 6,
  },
  contactButton: {
    marginBottom: 8,
    width: '100%',
  },
  buttonsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 12,
  },
  actionsContainer: {
    marginTop: 8,
  },
  navigationSection: {
    marginBottom: 16,
  },
  feedbackSection: {
    marginBottom: 8,
  },
  actionSectionLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: '#666',
    marginBottom: 8,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  compactButtonRow: {
    flexDirection: 'row',
    gap: 8,
  },
  compactButtonColumn: {
    gap: 8,
  },
  compactButton: {
    flex: 1,
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  compactVerticalButton: {
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  actionButton: {
    marginRight: 8,
    marginBottom: 8,
    width: '100%',
  },
  buttonIcon: {
    marginRight: 6,
  },
  buttonText: {
    color: colors.accent,
  },
  accidentsSection: {
    marginBottom: 16,
  },
  accidentItem: {
    fontSize: 14,
    color: '#444',
    marginLeft: 12,
    marginTop: 4,
  },
  reportButton: {
    alignSelf: 'flex-start',
    width: '100%',
    marginBottom: 12,
  },
  noteText: {
    fontSize: 14,
    color: '#333',
    lineHeight: 20,
  },
  secondaryNote: {
    marginTop: 12,
  },
  payItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  payLabel: {
    fontSize: 14,
    color: '#444',
  },
  payValue: {
    fontSize: 14,
    fontWeight: '500',
    color: '#333',
  },
  totalPay: {
    borderBottomWidth: 0,
    marginTop: 4,
  },
  totalLabel: {
    fontSize: 15,
    fontWeight: '700',
    color: '#000',
  },
  totalValue: {
    fontSize: 16,
    fontWeight: '700',
    color: '#000',
  },
  crewItem: {
    paddingBottom: 16,
  },
  crewItemBorder: {
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
    marginBottom: 16,
  },
  crewHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  crewImageContainer: {
    marginRight: 12,
  },
  crewImage: {
    width: 60,
    height: 60,
    borderRadius: 30,
    borderWidth: 2,
    borderColor: '#f0f0f0',
  },
  crewImagePlaceholder: {
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: '#f0f0f0',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: '#f0f0f0'
  },
  crewName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#000',
    flexShrink: 1,
    flexWrap: 'wrap',
  },
  crewLevel: {
    fontSize: 12,
    color: '#666',
  },
  crewButtons: {
    marginBottom: 8,
  },
  crewButton: {
    marginBottom: 8,
  },
  feedbackButton: {
    alignSelf: 'flex-start',
    width: '100%',
  },
  vehicleItem: {
    paddingBottom: 12,
  },
  vehicleItemBorder: {
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
    marginBottom: 12,
  },
  vehicleDetail: {
    flexDirection: 'row',
    marginBottom: 6,
  },
  vehicleLabel: {
    width: 100,
    fontSize: 14,
    fontWeight: '600',
    color: '#444',
  },
  vehicleValue: {
    flex: 1,
    fontSize: 14,
    color: '#333',
  },
  documentItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  documentIconContainer: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  documentInfo: {
    flex: 1,
  },
  documentName: {
    fontSize: 14,
    fontWeight: '500',
    color: '#333',
    marginBottom: 4,
  },
  documentSize: {
    fontSize: 12,
    color: '#666',
  },
  crewLiftRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 4,
    marginTop: 4,
  },
  crewLiftBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    backgroundColor: '#f1f5f9',
    borderWidth: 1,
    borderColor: '#e2e8f0',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  crewLiftBadgeText: {
    fontSize: 11,
    fontWeight: '600',
    color: '#475569',
  },
  badgeContainer: {
    backgroundColor: colors.primary,
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
    marginLeft: 8,
  },
  badgeText: {
    color: 'white',
    fontWeight: 'bold',
    fontSize: 12,
  },
  noteContainer: {
    marginBottom: 16,
    padding: 16,
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e9ecef',
  },
  noteHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  noteLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
  },
  copyButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: 'white',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: colors.primary,
  },
  copyButtonText: {
    fontSize: 12,
    fontWeight: '500',
    color: colors.primary,
    marginLeft: 4,
  },
  groupMessageSection: {
    marginBottom: 16,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  groupMessageButton: {
    width: '100%',
  },
});